import { TFunction } from 'i18next';
import { HCConcept, HCForms, HCVisit } from '../../models/Entities/Protocols/HCVisit';
import { ContractConceptRelationship } from '../../models/Entities/Contracts/ContractConceptRelationship';
import { ExtraFormType } from '../../models/Enums/ExtraFormType';

// Lógica pura del árbol de conceptos de la visita (sin React) para poder probarla sola.

/** Grupo de formularios: los propios de la visita o uno de los tipos extra del EMR. */
export type FormGroupKey = 'visit' | ExtraFormType;

export interface FormGroup {
  key: FormGroupKey;
  forms: HCForms[];
}

export interface ConceptCounts {
  total: number;
  realized: number;
}

/** Tipo de concepto que el EMR marca como campo pagable del formulario. */
export const PAYABLE_FIELD_TYPE = 'PayableField';

/** Etiqueta que el EMR usa para el concepto "realizado" sin descripción propia. */
const REALIZED_LABEL = 'REALIZED';

const hasExtraFormType = (value: ExtraFormType | null | undefined): value is ExtraFormType => value !== null && value !== undefined;

/**
 * Tipo extra efectivo de un formulario. Algunos ambientes del EMR lo mandan sólo en los conceptos,
 * por eso se cae al primer concepto que lo traiga.
 */
export const effectiveExtraFormType = (form: HCForms): ExtraFormType | undefined => {
  if (hasExtraFormType(form.extraFormType)) return form.extraFormType;
  const fromConcept = (form.concepts ?? []).find((concept) => concept && hasExtraFormType(concept.extraFormType));
  return fromConcept?.extraFormType ?? undefined;
};

/** Agrupa los formularios: primero los de la visita, después los extra en el orden del enum. */
export const groupForms = (forms: HCForms[] | null | undefined): FormGroup[] => {
  const groups = new Map<FormGroupKey, HCForms[]>();
  (forms ?? []).forEach((form) => {
    if (!form) return;
    const key: FormGroupKey = effectiveExtraFormType(form) ?? 'visit';
    const bucket = groups.get(key) ?? [];
    bucket.push(form);
    groups.set(key, bucket);
  });
  const rank = (key: FormGroupKey) => (key === 'visit' ? -1 : key);
  return Array.from(groups.entries())
    .sort(([a], [b]) => rank(a) - rank(b))
    .map(([key, grouped]) => ({ key, forms: grouped }));
};

/** Conceptos no nulos del formulario (el EMR a veces manda huecos en la colección). */
export const formConcepts = (form: HCForms): HCConcept[] => (form.concepts ?? []).filter((concept): concept is HCConcept => !!concept);

export const countConcepts = (form: HCForms): ConceptCounts => {
  const concepts = formConcepts(form);
  return { total: concepts.length, realized: concepts.filter((concept) => !!concept.realized).length };
};

export const countVisitConcepts = (visit: Pick<HCVisit, 'forms'>): ConceptCounts =>
  (visit.forms ?? []).reduce(
    (acc, form) => {
      if (!form) return acc;
      const counts = countConcepts(form);
      return { total: acc.total + counts.total, realized: acc.realized + counts.realized };
    },
    { total: 0, realized: 0 }
  );

/** Clave estable del concepto en el árbol: el campo del EMR cuando viene, si no el id del concepto y su posición. */
export const conceptKey = (form: Pick<HCForms, 'patientFormId'>, concept: HCConcept, index: number): string =>
  `concept-${form.patientFormId}-${concept.fieldId || concept.conceptId || 'c'}-${index}`;

/** Texto del concepto; el EMR manda la descripción vacía para el concepto "realizado". */
export const conceptLabel = (concept: Pick<HCConcept, 'description'>, t: TFunction): string => {
  const description = (concept.description ?? '').trim();
  return description.length > 0 ? description : t('visitConcepts.tree.realized');
};

const matchesRelationship = (
  relationship: ContractConceptRelationship,
  label: string,
  formName: string | undefined,
  itemNumber: number | undefined
): boolean => {
  const normalized = label.trim().toUpperCase();
  if (relationship.formConcept?.toUpperCase() !== normalized) return false;
  // "Realizado" se repite en cada formulario: sólo cuenta la relación del mismo formulario y posición.
  if (normalized === REALIZED_LABEL && formName !== undefined && itemNumber !== undefined) {
    return relationship.formName?.toLowerCase() === formName.toLowerCase() && relationship.itemNumber === itemNumber;
  }
  return true;
};

/**
 * Facturable: el formato nuevo del EMR lo trae resuelto en el concepto (`isPayable` / `contractConceptId`);
 * para respuestas viejas se busca la relación del contrato por nombre de concepto.
 */
export const isPayableConcept = (
  concept: Pick<HCConcept, 'isPayable' | 'contractConceptId' | 'description'>,
  relationships: ContractConceptRelationship[] | undefined,
  label: string,
  formName?: string,
  itemNumber?: number
): boolean => {
  if (concept.isPayable || (concept.contractConceptId !== undefined && concept.contractConceptId !== null)) return true;
  return (relationships ?? []).some((relationship) => relationship.isPayable && matchesRelationship(relationship, label, formName, itemNumber));
};

/** Prestación (servicio médico de un proveedor), con la misma regla de resolución que `isPayableConcept`. */
export const isProcedureConcept = (
  concept: Pick<HCConcept, 'isProcedure' | 'medicalServiceId'>,
  relationships: ContractConceptRelationship[] | undefined,
  label: string,
  formName?: string,
  itemNumber?: number
): boolean => {
  if (concept.isProcedure || (concept.medicalServiceId !== undefined && concept.medicalServiceId !== null)) return true;
  return (relationships ?? []).some((relationship) => relationship.isProcedure && matchesRelationship(relationship, label, formName, itemNumber));
};

// ---- autor del formulario (EMR createdById / modifiedById → profesional) ----

export interface FormAuthor {
  id: string;
  name: string;
}

export interface FormAuthors {
  /** Quien completó el formulario. */
  created?: FormAuthor;
  /** Último que lo modificó (sólo si se pudo resolver). */
  modified?: FormAuthor;
}

/** "Juan Pérez" → "JP", "Pérez, Juan Carlos" → "JP" (apellido, nombre), "Ana" → "A". Máximo 2 letras. */
export const initialsOf = (fullName: string | null | undefined): string => {
  const raw = (fullName ?? '').trim();
  if (!raw) return '';
  // Formato "Apellido, Nombre": se invierte para que las iniciales queden nombre + apellido.
  const parts = raw.includes(',')
    ? raw.split(',').reverse().flatMap((part) => part.trim().split(/\s+/))
    : raw.split(/\s+/);
  const words = parts.filter((word) => /[\p{L}\p{N}]/u.test(word));
  if (words.length === 0) return '';
  const first = words[0];
  const last = words.length > 1 ? words[words.length - 1] : undefined;
  return ((first[0] ?? '') + (last?.[0] ?? '')).toUpperCase();
};

/** Resuelve los ids de usuario del EMR contra el listado de profesionales (por userId, sin distinguir mayúsculas). */
export const formAuthors = (
  form: Pick<HCForms, 'createdById' | 'modifiedById'>,
  practitioners: { userId: string; fullName?: string | null }[] | undefined
): FormAuthors => {
  const resolve = (id: string | null | undefined): FormAuthor | undefined => {
    if (!id) return undefined;
    const name = practitioners?.find((practitioner) => practitioner.userId?.toLowerCase() === id.toLowerCase())?.fullName?.trim();
    return name ? { id, name } : undefined;
  };
  return { created: resolve(form.createdById), modified: resolve(form.modifiedById) };
};

/** Clave i18n del tipo de visita del EMR (0 presencial, 1 telemedicina, 2 domiciliaria, otro). */
export const visitTypeKey = (visitType: number | null | undefined): 'inPerson' | 'telehealth' | 'homeVisit' | 'other' => {
  switch (visitType) {
    case 0:
      return 'inPerson';
    case 1:
      return 'telehealth';
    case 2:
      return 'homeVisit';
    default:
      return 'other';
  }
};

/** Clave i18n del grupo de formularios. */
export const formGroupKey = (key: FormGroupKey): string => {
  switch (key) {
    case 'visit':
      return 'visitConcepts.extraForms.visit';
    case ExtraFormType.Additional:
      return 'visitConcepts.extraForms.additional';
    case ExtraFormType.ExtraProtocolVisit:
      return 'visitConcepts.extraForms.extraProtocolVisit';
    case ExtraFormType.EarlyCompletion:
      return 'visitConcepts.extraForms.earlyCompletion';
    default:
      return 'visitConcepts.extraForms.visit';
  }
};
